import { useNavigate } from "react-router-dom";
import { useLex } from "../../lib/theme/ThemeProvider";
import type { CommandKey } from "../../lib/theme/themes";

// CommandKey now lives in the theme lexicon (stable across skins). Re-exported
// here so existing imports (CommandLayout) keep working unchanged.
export type { CommandKey };

interface CommandStyle {
  key: CommandKey;
  accent: "ember" | "blue";
}

// Order + accent are presentation concerns and stay theme-agnostic.
// The verb/description text comes from the active lexicon.
const COMMAND_ORDER: CommandStyle[] = [
  { key: "raven", accent: "blue" },
  { key: "hounds", accent: "ember" },
  { key: "horn", accent: "blue" },
  { key: "shield-wall", accent: "blue" },
  { key: "arrows", accent: "ember" },
  { key: "oathbreaker", accent: "ember" },
  { key: "berserkrs", accent: "ember" },
];

interface Props {
  caseId: string;
  /** When set, that command renders as active (stronger border, no nav). */
  currentCommand?: CommandKey;
}

function CommandBar({ caseId, currentCommand }: Props) {
  const lex = useLex();
  return (
    <div className="rounded border border-steel-dark bg-iron/70 backdrop-blur-sm">
      <div className="flex items-center justify-between border-b border-steel-dark/70 px-5 py-3 font-display text-base font-light uppercase tracking-[0.16em] text-bone-dim">
        <span>{lex.throneLine}</span>
        <span className="text-bone-mute/70">{lex.commandsAvailable}</span>
      </div>
      <div className="grid grid-cols-2 gap-px bg-steel-dark md:grid-cols-3 xl:grid-cols-7">
        {COMMAND_ORDER.map((cmd) => (
          <CommandButton
            key={cmd.key}
            command={cmd}
            caseId={caseId}
            active={cmd.key === currentCommand}
          />
        ))}
      </div>
    </div>
  );
}

function CommandButton({
  command,
  caseId,
  active,
}: {
  command: CommandStyle;
  caseId: string;
  active: boolean;
}) {
  const navigate = useNavigate();
  const lex = useLex();
  const copy = lex.commands[command.key];

  const accentBorder =
    command.accent === "ember"
      ? "hover:border-ember-bright/60 hover:shadow-ember-soft hover:text-ember-bright"
      : "hover:border-blue-bright/60 hover:text-blue-bright";

  const activeStyles = active
    ? command.accent === "ember"
      ? "!border-ember-bright/70 bg-ember/30 text-ember-bright shadow-ember-soft"
      : "!border-blue-bright/70 bg-iron-2/80 text-blue-bright"
    : "border-transparent bg-iron/70";

  return (
    <button
      type="button"
      onClick={() => navigate(`/case/${caseId}/${command.key}`)}
      disabled={active}
      className={`group flex flex-col items-start gap-1 border px-5 py-5 text-left transition-all ${activeStyles} ${active ? "" : accentBorder}`}
    >
      <span className="font-display text-lg font-light tracking-tight">
        {copy.verb}
      </span>
      <span className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/70">
        {active ? "// active" : copy.description}
      </span>
    </button>
  );
}

export default CommandBar;
